import axios, { AxiosResponse } from 'axios'

import { env } from '@js/utils/env'
import gql from '@js/api/utils/gql'
import getStoreName from '@js/api/utils/getStoreName'
import getBearerToken from '@js/api/utils/getBearerToken'

import { CustomerRegistration } from '@js/api/types/customer/CustomerRegistration'

interface RegisterAndUpsertItemToCartPayload {
  cartId: string
  sku: string
  courseOption: string
  craftEntryId: number
  qty: number
  registrationId: number|null
  personal: {
    upsert_address: boolean
    country?: string
    address?: string
    address_two?: string
    city?: string
    state?: string
    postcode?: string
    phone?: string
    secondary_phone?: string
  }
  professional: {
    licenses?: number[],
    management_level?: string
    primary_employment?: string
    employer_name?: string
    employer_city?: string
    employer_state?: string
    hear_about?: string
    certifications?: string[]
  }
}

interface RegisterAndUpsertItemToCartResponse {
  data: {
    wceiRegisterAndUpsertItemToCart: CustomerRegistration | null
  }
  errors?: [{ message: string }]
}

export const registerAndUpsertItemToCartMutation = gql`
  mutation wceiRegisterAndUpsertItemToCart($input: WceiRegistrationInput!) {
    wceiRegisterAndUpsertItemToCart(input: $input) {
      registration_id
      craft_entry_id
      personal {
        country
        address
        postcode
        state
        address_two
        city
        phone
        secondary_phone
      }
      professional {
        licenses {
          license_id
          license_number
          license_type
          license_state
        }
        certifications
        employer_city
        employer_name
        employer_state
        hear_about
        management_level
        primary_employment
      }
      item {
        sku
        course_option
      }
    }
  }
`

/**
 * @throws {string}
 *
 * Will throw one error message; Magento only returns one at a time, rather than an array of all errors.
 */
export const registerAndUpsertItemToCart = async (payload: RegisterAndUpsertItemToCartPayload): Promise<CustomerRegistration | null> => {
  const bearerToken = getBearerToken()

  const { data }: AxiosResponse<RegisterAndUpsertItemToCartResponse> = await axios({
    method: 'POST',
    url: env.magento.GRAPHQL_URL,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${bearerToken}`,
      Store: getStoreName(),
    },
    data: {
      query: registerAndUpsertItemToCartMutation,
      variables: {
        input: {
          cart_id: payload.cartId,
          course_option: payload.courseOption,
          sku: payload.sku,
          craft_entry_id: payload.craftEntryId,
          qty: payload.qty,
          registration_id: payload.registrationId,
          personal: payload.personal,
          professional: payload.professional,
        },
      },
    },
  })

  if (data.errors) {
    throw data.errors[0].message
  }

  if (data.data.wceiRegisterAndUpsertItemToCart) {
    return data.data.wceiRegisterAndUpsertItemToCart
  }

  return null
}
